import React, {
  Ref,
  HTMLAttributes,
  forwardRef,
  ReactNode,
  useCallback,
} from "react";

import classnames from "classnames";

import { StyledEntityCard } from "./Styles";

export type cardSection = "header" | "body" | "footer";

export interface IEntityProperties {
  name: string;
  render: () => ReactNode;
  showInList?: boolean;
  cardSection: cardSection;
  className?: string;
}

export interface IEntityCardProps {
  renderAs?: "list-item" | "grid-item";
  properties: IEntityProperties[];
  ref?: Ref<HTMLDivElement>;
  link?: string;
  className?: string;
}

export const EntityCard = forwardRef(
  (
    {
      renderAs = "list-item",
      properties,
      className,
    }: HTMLAttributes<HTMLDivElement> & IEntityCardProps,
    ref: Ref<HTMLDivElement> | null | undefined
  ) => {
    const renderAsListItem = useCallback((properties: IEntityProperties[]) => {
      return properties
        .filter((p) => p.showInList)
        .map((p) => {
          return (
            <div key={p.name} className={`entity-property-${p.name}`}>
              {p.render()}
            </div>
          );
        });
    }, []);

    const renderAsGridItem = useCallback((properties: IEntityProperties[]) => {
      const headerSection = properties.filter(
        (p: IEntityProperties) => p.cardSection === "header"
      );
      const bodySection = properties.filter(
        (p: IEntityProperties) => p.cardSection === "body"
      );
      const footerSection = properties.filter(
        (p) => p.cardSection === "footer"
      );
      const renderProperty = (p: IEntityProperties): ReactNode => (
        <div key={p.name} className={`entity-property-${p.name}`}>
          {p.render()}
        </div>
      );
      return (
        <>
          <div className="card-header">
            {headerSection.map((p) => renderProperty(p))}
          </div>
          <div className="card-body">
            {bodySection.map((p) => renderProperty(p))}
          </div>
          <div className="card-footer">
            {footerSection.map((p) => renderProperty(p))}
          </div>
        </>
      );
    }, []);

    return (
      <StyledEntityCard
        ref={ref}
        hoverable
        className={classnames(
          "entity-card-wrapper",
          {
            "grid-item": renderAs === "grid-item",
            "list-item": renderAs === "list-item",
          },
          className
        )}
        variant={renderAs === "grid-item" ? "paper-1" : "paper-2"}
        $renderAs={renderAs}
      >
        {renderAs === "list-item" ? renderAsListItem(properties) : null}
        {renderAs === "grid-item" ? renderAsGridItem(properties) : null}
      </StyledEntityCard>
    );
  }
);

EntityCard.displayName = "EntityCard";
